/**
 * The Worker client. Knows nothing about decryption — it moves bytes.
 */
import type { SnapshotMeta } from "@tally/shared";

export class ApiError extends Error {
  readonly status: number;
  constructor(status: number, message: string) {
    super(message);
    this.name = "ApiError";
    this.status = status;
  }
}

export type ApiConfig = { baseUrl: string; readToken: string };

export type CompanyRow = SnapshotMeta & { companyId: string };

function headers(config: ApiConfig): HeadersInit {
  return { Authorization: `Bearer ${config.readToken}` };
}

function explain(status: number): string {
  switch (status) {
    case 401:
      return "That read token was rejected. Check it in Settings.";
    case 404:
      return "No snapshot for this company yet — has the laptop agent run?";
    default:
      return `The server answered ${status}.`;
  }
}

async function request(config: ApiConfig, path: string, extra?: HeadersInit): Promise<Response> {
  const url = `${config.baseUrl.replace(/\/+$/, "")}${path}`;
  let response: Response;
  try {
    response = await fetch(url, { headers: { ...headers(config), ...extra } });
  } catch {
    // Offline is normal here, not exceptional — every screen still works from
    // IndexedDB, so this must be reported as a state, not a crash.
    throw new ApiError(0, "Could not reach the server. Showing the last synced copy.");
  }
  if (response.status === 304) return response;
  if (!response.ok) throw new ApiError(response.status, explain(response.status));
  return response;
}

export async function fetchCompanies(config: ApiConfig): Promise<CompanyRow[]> {
  const response = await request(config, "/v1/companies");
  return (await response.json()) as CompanyRow[];
}

export async function fetchMeta(config: ApiConfig, companyId: string): Promise<SnapshotMeta> {
  const response = await request(config, `/v1/meta/${encodeURIComponent(companyId)}`);
  return (await response.json()) as SnapshotMeta;
}

/**
 * Ask the laptop agent to extract and push now.
 *
 * The laptop opens no inbound ports, so this cannot reach it directly. It
 * leaves a flag on the Worker which the agent picks up on its next poll —
 * within about a minute, against the 15 minutes of waiting for the next
 * scheduled run.
 */
export async function requestSync(config: ApiConfig, companyId: string): Promise<string> {
  const url = `${config.baseUrl.replace(/\/+$/, "")}/v1/request-sync/${encodeURIComponent(companyId)}`;
  let response: Response;
  try {
    response = await fetch(url, { method: "POST", headers: headers(config) });
  } catch {
    throw new ApiError(0, "Could not reach the server to ask for a sync.");
  }
  if (!response.ok) throw new ApiError(response.status, explain(response.status));
  const body = (await response.json()) as { requestedAt?: string };
  return body.requestedAt ?? new Date().toISOString();
}

export type PullResult =
  | { changed: false }
  | { changed: true; blob: Uint8Array; hash: string | null };

/**
 * `If-None-Match` turns an unchanged snapshot into a 304 with no body — the
 * whole point of hashing the plaintext rather than the ciphertext.
 */
export async function pullSnapshot(
  config: ApiConfig,
  companyId: string,
  knownHash: string | null,
): Promise<PullResult> {
  const response = await request(
    config,
    `/v1/pull/${encodeURIComponent(companyId)}`,
    knownHash ? { "If-None-Match": `"${knownHash}"` } : undefined,
  );

  if (response.status === 304) return { changed: false };

  const buffer = await response.arrayBuffer();
  const etag = response.headers.get("ETag");
  return {
    changed: true,
    blob: new Uint8Array(buffer),
    hash: response.headers.get("X-Snapshot-Hash") ?? (etag ? etag.replace(/"/g, "") : null),
  };
}
